import { useContext, useEffect, useState } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    FormGroup,
    Label,
    Input,
    FormFeedback,
    Card,
    CardBody,
    Row,
    Col,
} from 'reactstrap';
import { useForm, Controller } from 'react-hook-form';
import IntlCurrencyInput from 'react-intl-currency-input';
import Dropzone, { type Accept, type FileRejection } from 'react-dropzone';
import imageCompression from 'browser-image-compression';
import { currencyConfig } from '~/shared/components/form/Inputs';
import { RegistrationOption } from '~/types/campaign';
import { CampaignContext } from '~/context/campaign';
import {
    fetchFeatureImagePresignedUrl,
    uploadFile,
} from '~/services/campaign';
import { renderIcon } from '~/shared/components/icons';
import { getFilename } from '~/services/helper';
import { normalizeFileName } from '~/shared/utils';
import { Uploading } from '~/shared/components/Loading';
import { useToasts } from 'react-toast-notifications';
import { AdminContext } from '~/context/admin';
import {
    MAX_UPLOAD_BYTES,
    MISSING_CAMPAIGN_MESSAGE,
    describeRejections,
    resolveCampaignId,
    uploadErrorMessage,
} from './uploadHelpers';

type RegistrationOptionFormData = {
    name: string;
    price: number;
    quantity: number | null;
    attendees_per_item: number | null;
    description: string;
    option_question: string;
    image: string | null;
    is_visible: boolean;
    sale_name: string;
    sale_price: number | null;
    sale_start_date: string;
    sale_end_date: string;
};

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onSave: (option: Partial<RegistrationOption>) => void;
    option?: RegistrationOption;
};

const resizeImage = async (file: File) => {
    const options = {
        maxSizeMB: 2,
        maxWidthOrHeight: 2000,
        useWebWorker: true,
    };
    const compressedFile = await imageCompression(file, options);
    return compressedFile;
};

export const RegistrationOptionModal = ({
    isOpen,
    onClose,
    onSave,
    option,
}: Props) => {
    const [{ campaign }] = useContext(CampaignContext);
    const [{ campaignId: adminCampaignId }] = useContext(AdminContext);
    const { addToast } = useToasts();
    const [savingImage, setSavingImage] = useState(false);
    const [imgUrl, setImgUrl] = useState<string | null>(option?.image || null);
    const [saleEnabled, setSaleEnabled] = useState(
        option?.sale_price != null
    );
    const isEditing = !!option?.id;

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        clearErrors,
        formState: { errors },
    } = useForm<RegistrationOptionFormData>({
        defaultValues: {
            name: option?.name || '',
            price: option?.price || 0,
            quantity: option?.quantity || null,
            attendees_per_item: option?.attendees_per_item ?? null,
            description: option?.description || '',
            option_question: option?.option_question || '',
            image: option?.image || null,
            is_visible: option?.is_visible ?? true,
            sale_name: option?.sale_name || '',
            sale_price: option?.sale_price ?? null,
            sale_start_date: option?.sale_start_date || '',
            sale_end_date: option?.sale_end_date || '',
        },
        mode: 'onSubmit',
    });

    const quantityValue = watch('quantity');

    useEffect(() => {
        if (isOpen) {
            // Clear any existing errors first
            clearErrors();
            if (option) {
                reset({
                    name: option.name || '',
                    price: option.price || 0,
                    quantity: option.quantity,
                    attendees_per_item: option.attendees_per_item ?? null,
                    description: option.description || '',
                    option_question: option.option_question || '',
                    image: option.image || null,
                    is_visible: option.is_visible ?? true,
                    sale_name: option.sale_name || '',
                    sale_price: option.sale_price ?? null,
                    sale_start_date: option.sale_start_date || '',
                    sale_end_date: option.sale_end_date || '',
                });
                setImgUrl(option.image || null);
                setSaleEnabled(option.sale_price != null);
            } else {
                reset({
                    name: '',
                    price: 0,
                    quantity: null,
                    attendees_per_item: null,
                    description: '',
                    option_question: '',
                    image: null,
                    is_visible: true,
                    sale_name: '',
                    sale_price: null,
                    sale_start_date: '',
                    sale_end_date: '',
                });
                setImgUrl(null);
                setSaleEnabled(false);
            }
        }
    }, [isOpen, option, reset, clearErrors]);

    const handleImageDropRejected = (rejections: FileRejection[]) => {
        addToast(describeRejections(rejections, 'a JPG, PNG or GIF image'), {
            appearance: 'error',
        });
    };

    const handleImageDrop = async (files: File[]) => {
        const campaignId = resolveCampaignId(campaign, adminCampaignId);
        if (!campaignId) {
            addToast(MISSING_CAMPAIGN_MESSAGE, { appearance: 'error' });
            return;
        }

        setSavingImage(true);
        let currentFile: File | undefined;
        try {
            for (const file of files) {
                currentFile = file;
                const fileName = normalizeFileName(file);
                const { fields, url } = await fetchFeatureImagePresignedUrl(
                    fileName,
                    campaignId
                );
                const resized = await resizeImage(file);
                await uploadFile(fields, url, resized);
                const fullUrl = url + fields.key;
                setValue('image', fullUrl);
                setImgUrl(fullUrl);
            }
        } catch (e) {
            console.error('Error uploading image:', e);
            addToast(uploadErrorMessage(e, currentFile), {
                appearance: 'error',
            });
        } finally {
            setSavingImage(false);
        }
    };

    const clearImage = () => {
        setValue('image', null);
        setImgUrl(null);
    };

    const onSubmit = (data: RegistrationOptionFormData) => {
        const optionData: Partial<RegistrationOption> = {
            ...data,
            quantity: data.quantity || null,
            attendees_per_item: data.attendees_per_item || null,
            sale_name: saleEnabled ? data.sale_name : '',
            sale_price: saleEnabled ? data.sale_price : null,
            sale_start_date: saleEnabled
                ? data.sale_start_date || null
                : null,
            sale_end_date: saleEnabled ? data.sale_end_date || null : null,
            sort_order: option?.sort_order ?? null,
        };

        if (option?.id) {
            optionData.id = option.id;
            optionData.isDirty = true;
        }

        onSave(optionData);
        onClose();
    };

    const handleClose = () => {
        reset();
        setImgUrl(null);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} toggle={handleClose} size="lg">
            <ModalHeader toggle={handleClose}>
                {isEditing ? 'Edit Option' : 'Create Option'}
            </ModalHeader>
            <ModalBody>
                <p className="text-muted mb-4">
                    Create registration options attendees can purchase or
                    reserve when registering for your fundraiser. Set the price,
                    quantity limit, description, and image for each option.
                </p>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormGroup>
                        <Label for="name">
                            Name <span className="text-danger">*</span>
                        </Label>
                        <Controller
                            name="name"
                            control={control}
                            rules={{ required: 'Name is required' }}
                            render={({ field }) => (
                                <Input
                                    id="name"
                                    placeholder="Individual Registration"
                                    {...field}
                                    invalid={!!errors.name}
                                />
                            )}
                        />
                        {errors.name && (
                            <FormFeedback>{errors.name.message}</FormFeedback>
                        )}
                    </FormGroup>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="price">
                                    Price <span className="text-danger">*</span>
                                </Label>
                                <Controller
                                    name="price"
                                    control={control}
                                    rules={{
                                        required: 'Price is required',
                                        min: {
                                            value: 0,
                                            message: 'Price cannot be negative',
                                        },
                                    }}
                                    render={({ field }) => (
                                        <IntlCurrencyInput
                                            className={`form-control ${
                                                errors.price ? 'is-invalid' : ''
                                            }`}
                                            currency="USD"
                                            config={currencyConfig}
                                            value={field.value}
                                            onChange={(
                                                _event: any,
                                                value: number
                                            ) => field.onChange(value)}
                                        />
                                    )}
                                />
                                {errors.price && (
                                    <FormFeedback className="d-block">
                                        {errors.price.message}
                                    </FormFeedback>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="quantity">Quantity</Label>
                                <div className="d-flex align-items-center">
                                    <Controller
                                        name="quantity"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                id="quantity"
                                                type="number"
                                                min="0"
                                                placeholder="0"
                                                value={field.value ?? ''}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    field.onChange(val === '' ? null : parseInt(val));
                                                }}
                                                style={{ width: '100px' }}
                                            />
                                        )}
                                    />
                                    <span className="ms-2 text-muted">
                                        / {quantityValue || 0}
                                    </span>
                                </div>
                                <small className="text-muted">
                                    Leave blank for unlimited
                                </small>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="attendees_per_item">
                                    Attendees per Option
                                </Label>
                                <Controller
                                    name="attendees_per_item"
                                    control={control}
                                    rules={{
                                        min: {
                                            value: 1,
                                            message: 'Must be at least 1',
                                        },
                                    }}
                                    render={({ field }) => (
                                        <Input
                                            id="attendees_per_item"
                                            type="number"
                                            min="1"
                                            placeholder="1"
                                            value={field.value ?? ''}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                field.onChange(
                                                    val === ''
                                                        ? null
                                                        : parseInt(val)
                                                );
                                            }}
                                            invalid={!!errors.attendees_per_item}
                                            style={{ width: '100px' }}
                                        />
                                    )}
                                />
                                {errors.attendees_per_item && (
                                    <FormFeedback className="d-block">
                                        {errors.attendees_per_item.message}
                                    </FormFeedback>
                                )}
                                <small className="text-muted">
                                    Enter the number of attendees for this
                                    option. For example: Individual
                                    Registration includes 1 attendee and a
                                    Group Registration may include 4. Leave
                                    blank for 1.
                                </small>
                            </FormGroup>
                        </Col>
                    </Row>

                    <FormGroup check className="mb-3">
                        <Input
                            type="switch"
                            id="schedule_sale_price"
                            checked={saleEnabled}
                            onChange={(e) => {
                                setSaleEnabled(e.target.checked);
                                if (!e.target.checked) {
                                    clearErrors([
                                        'sale_price',
                                        'sale_start_date',
                                        'sale_end_date',
                                    ]);
                                }
                            }}
                        />
                        <Label
                            check
                            for="schedule_sale_price"
                            className="ms-2 fw-bold"
                        >
                            Offer Promotional Pricing
                        </Label>
                    </FormGroup>

                    {saleEnabled && (
                        <div
                            className="ps-3 mb-3"
                            style={{ borderLeft: '2px solid #dee2e6' }}
                        >
                            <FormGroup>
                                <Label for="sale_name">Promotion Name</Label>
                                <Controller
                                    name="sale_name"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            id="sale_name"
                                            placeholder="Early Bird Deal"
                                            {...field}
                                        />
                                    )}
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label for="sale_price">
                                    Promotion Price{' '}
                                    <span className="text-danger">*</span>
                                </Label>
                                <Controller
                                    name="sale_price"
                                    control={control}
                                    rules={{
                                        validate: (value) => {
                                            if (!saleEnabled) return true;
                                            if (value == null || value <= 0) {
                                                return 'Promotion price is required';
                                            }
                                            return true;
                                        },
                                    }}
                                    render={({ field }) => (
                                        <IntlCurrencyInput
                                            className={`form-control ${
                                                errors.sale_price
                                                    ? 'is-invalid'
                                                    : ''
                                            }`}
                                            currency="USD"
                                            config={currencyConfig}
                                            value={field.value ?? 0}
                                            onChange={(
                                                _event: any,
                                                value: number
                                            ) => field.onChange(value)}
                                        />
                                    )}
                                />
                                {errors.sale_price && (
                                    <FormFeedback className="d-block">
                                        {errors.sale_price.message}
                                    </FormFeedback>
                                )}
                            </FormGroup>

                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label for="sale_start_date">
                                            Promotion Start Date
                                        </Label>
                                        <Controller
                                            name="sale_start_date"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    id="sale_start_date"
                                                    type="date"
                                                    {...field}
                                                />
                                            )}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label for="sale_end_date">
                                            Promotion End Date
                                        </Label>
                                        <Controller
                                            name="sale_end_date"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    id="sale_end_date"
                                                    type="date"
                                                    {...field}
                                                />
                                            )}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <small className="text-muted">
                                When the promotion ends, prices and option names
                                will revert to their original value.
                            </small>
                        </div>
                    )}

                    <FormGroup>
                        <Label for="description">
                            Description <span className="text-danger">*</span>
                        </Label>
                        <Controller
                            name="description"
                            control={control}
                            rules={{ required: 'Description is required' }}
                            render={({ field }) => (
                                <Input
                                    id="description"
                                    type="textarea"
                                    rows={4}
                                    placeholder="Text"
                                    {...field}
                                    invalid={!!errors.description}
                                />
                            )}
                        />
                        {errors.description && (
                            <FormFeedback>
                                {errors.description.message}
                            </FormFeedback>
                        )}
                    </FormGroup>

                    <FormGroup>
                        <Label for="option_question">Option Question</Label>
                        <Controller
                            name="option_question"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    id="option_question"
                                    placeholder="Attendee Name(s)"
                                    {...field}
                                />
                            )}
                        />
                        <small className="text-muted">
                            A question to ask registrants, e.g., "Attendee
                            Name(s)"
                        </small>
                    </FormGroup>

                    <FormGroup>
                        <Label>Image</Label>
                        <Dropzone
                            onDrop={handleImageDrop}
                            onDropRejected={handleImageDropRejected}
                            maxSize={MAX_UPLOAD_BYTES}
                            accept={{ 'image/*': ['.png', '.jpg', '.jpeg', '.gif'] }}
                            multiple={false}
                            disabled={!!imgUrl}
                        >
                            {({ getRootProps, getInputProps }) => (
                                <Card
                                    style={{
                                        border: '2px dashed #949599',
                                        background: '#F0F6F6',
                                    }}
                                >
                                    <CardBody>
                                        <div {...getRootProps()}>
                                            <input {...getInputProps()} />
                                            <div className="text-center">
                                                {savingImage ? (
                                                    <Uploading />
                                                ) : imgUrl ? (
                                                    <div
                                                        className="position-relative mx-auto"
                                                        style={{
                                                            width: '220px',
                                                        }}
                                                    >
                                                        <img
                                                            src={imgUrl}
                                                            alt=""
                                                            style={{
                                                                height: '150px',
                                                                width: '150px',
                                                                objectFit:
                                                                    'cover',
                                                            }}
                                                        />
                                                        <div className="small my-2">
                                                            {getFilename(
                                                                imgUrl
                                                            )}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="text-danger fs-4 btn btn-link p-0 position-absolute top-0 end-0"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                clearImage();
                                                            }}
                                                        >
                                                            {renderIcon(
                                                                'close'
                                                            )}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="text-primary fs-4">
                                                            {renderIcon(
                                                                'upload'
                                                            )}
                                                        </div>
                                                        <p className="fs-6 mb-0">
                                                            <strong>
                                                                Click to upload
                                                            </strong>{' '}
                                                            or drag and drop
                                                            <br /> PNG, JPG, or
                                                            GIF (max 2000 x
                                                            2000px)
                                                        </p>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            )}
                        </Dropzone>
                    </FormGroup>

                    <FormGroup check className="mb-3">
                        <Controller
                            name="is_visible"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    type="switch"
                                    id="is_visible"
                                    checked={field.value}
                                    onChange={(e) =>
                                        field.onChange(e.target.checked)
                                    }
                                />
                            )}
                        />
                        <Label check for="is_visible" className="ms-2">
                            Visible to the public
                        </Label>
                    </FormGroup>
                </form>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" outline onClick={handleClose}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    onClick={handleSubmit(onSubmit)}
                >
                    Save
                </Button>
            </ModalFooter>
        </Modal>
    );
};
